/**
 * LoveLab page transitions — React PageTransition.tsx parity for MPA navigations.
 * Outbound: logo overlay on internal link click, then navigate.
 * Inbound: resume overlay from sessionStorage and fade out.
 */
(function () {
  var STORAGE_KEY = 'llPageTransition';
  var overlay = null;
  var busy = false;
  var timers = [];

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isMobile() {
    return window.innerWidth < 768;
  }

  function clearTimers() {
    timers.forEach(function (id) {
      clearTimeout(id);
    });
    timers = [];
  }

  function later(fn, ms) {
    var id = setTimeout(fn, ms);
    timers.push(id);
    return id;
  }

  function getOverlay() {
    if (!overlay) overlay = document.getElementById('ll-page-transition');
    return overlay;
  }

  function setActive(on) {
    document.documentElement.classList.toggle('ll-pt-active', !!on);
  }

  function markPending() {
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch (e) {}
  }

  function clearPending() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  function wasPending() {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function showOverlay(withLogo) {
    var el = getOverlay();
    if (!el) return;
    el.classList.add('is-visible');
    el.classList.remove('is-leaving', 'is-no-logo');
    if (withLogo) {
      el.classList.add('is-logo-in');
    } else {
      el.classList.remove('is-logo-in');
    }
    setActive(true);
  }

  function hideOverlay() {
    var el = getOverlay();
    if (!el) return;
    el.classList.remove('is-visible', 'is-logo-in', 'is-leaving', 'is-no-logo');
    setActive(false);
    busy = false;
  }

  /* React ScrollToTop parity — land at top on every navigation */
  function scrollToTop() {
    try {
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    } catch (e) {}
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    var lenis = window.lenis;
    if (lenis && typeof lenis.scrollTo === 'function') {
      lenis.scrollTo(0, { immediate: true });
      if (typeof lenis.scroll !== 'undefined') lenis.scroll = 0;
    }
  }

  function finishInbound() {
    var el = getOverlay();
    if (!el) {
      clearPending();
      setActive(false);
      scrollToTop();
      return;
    }

    clearPending();
    scrollToTop();
    showOverlay(true);

    var reduced = prefersReducedMotion() || isMobile();
    var hold = 50;
    var fadeOut = reduced ? 250 : 350;

    later(function () {
      el.classList.remove('is-logo-in');
      el.classList.add('is-leaving');

      later(function () {
        hideOverlay();
      }, fadeOut);
    }, hold);
  }

  /**
   * Full logo flash for browser back/forward (no outbound half from a click).
   * Matches React PageTransition on history pathname changes.
   */
  function playHistoryTransition() {
    if (busy) return;
    busy = true;
    clearTimers();
    clearPending();
    scrollToTop();

    var el = getOverlay();
    if (!el) {
      busy = false;
      setActive(false);
      return;
    }

    var reduced = prefersReducedMotion() || isMobile();
    var logoDelay = reduced ? 0 : 20;
    var hold = reduced ? 220 : 400;
    var fadeOut = reduced ? 250 : 350;

    showOverlay(false);

    later(function () {
      el.classList.add('is-logo-in');
    }, logoDelay);

    later(function () {
      el.classList.remove('is-logo-in');
      el.classList.add('is-leaving');

      later(function () {
        hideOverlay();
      }, fadeOut);
    }, logoDelay + hold);
  }

  function navigateTo(href) {
    window.location.href = href;
  }

  function startOutbound(href) {
    if (busy) return;
    busy = true;
    clearTimers();
    markPending();

    var el = getOverlay();
    if (!el) {
      navigateTo(href);
      return;
    }

    var reduced = prefersReducedMotion() || isMobile();
    var logoDelay = reduced ? 0 : 20;
    var navigateDelay = reduced ? 180 : 350;

    showOverlay(false);

    if (isChromeIos()) {
      el.classList.add('is-no-logo');
    } else {
      later(function () {
        el.classList.add('is-logo-in');
      }, logoDelay);
    }

    later(function () {
      navigateTo(href);
    }, navigateDelay);
  }

  function samePath(a, b) {
    return String(a || '').replace(/\/$/, '') === String(b || '').replace(/\/$/, '');
  }

  function isInternalNavLink(anchor, event) {
    if (!anchor || anchor.tagName !== 'A') return false;
    if (event.defaultPrevented) return false;
    if (event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (anchor.hasAttribute('download')) return false;
    if (anchor.getAttribute('data-no-transition') != null) return false;
    if (anchor.classList.contains('skip-to-content-link')) return false;

    var target = (anchor.getAttribute('target') || '').toLowerCase();
    if (target && target !== '_self') return false;

    var hrefAttr = anchor.getAttribute('href');
    if (!hrefAttr || hrefAttr === '#' || hrefAttr.charAt(0) === '#') return false;
    if (/^(mailto:|tel:|sms:|javascript:)/i.test(hrefAttr)) return false;

    var url;
    try {
      url = new URL(anchor.href, window.location.origin);
    } catch (e) {
      return false;
    }

    if (url.origin !== window.location.origin) return false;

    // Same-page hash / query-only changes — skip overlay
    if (
      samePath(url.pathname, window.location.pathname) &&
      url.search === window.location.search
    ) {
      return false;
    }

    return true;
  }

  function onClick(event) {
    if (window.Shopify && Shopify.designMode) return;
    if (busy) {
      event.preventDefault();
      return;
    }

    var anchor = event.target.closest && event.target.closest('a[href]');
    if (!isInternalNavLink(anchor, event)) return;

    event.preventDefault();
    startOutbound(anchor.href);
  }

  function isBackForwardNavigation() {
    try {
      var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      return !!(nav && nav.type === 'back_forward');
    } catch (e) {
      return false;
    }
  }

  function onPageShow(event) {
    if (window.Shopify && Shopify.designMode) {
      clearTimers();
      clearPending();
      hideOverlay();
      return;
    }

    // bfcache restore (Chrome back/forward): play full logo transition
    if (event.persisted) {
      clearTimers();
      busy = false;
      playHistoryTransition();
      return;
    }

    scrollToTop();
    requestAnimationFrame(scrollToTop);
    later(scrollToTop, 50);
    later(scrollToTop, 300);
  }

  /*
   * Chrome on iPhone (CriOS) moves the whole page under its toolbar while a new page loads, so a logo
   * on the outgoing page jumps however it is positioned (seen in screen recordings). There the
   * outgoing page shows the plain cover and the incoming page shows the logo.
   */
  function isChromeIos() {
    return /CriOS\//.test(navigator.userAgent);
  }

  function init() {
    overlay = document.getElementById('ll-page-transition');
    try {
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    } catch (e) {}

    scrollToTop();

    if (!overlay) {
      document.addEventListener('click', onClick, true);
      window.addEventListener('pageshow', onPageShow);
      return;
    }

    document.addEventListener('click', onClick, true);
    window.addEventListener('pageshow', onPageShow);

    if (window.Shopify && Shopify.designMode) {
      clearPending();
      hideOverlay();
      return;
    }

    // Click navigations set sessionStorage → short inbound fade.
    // Browser back/forward → full history transition (React parity).
    if (wasPending()) {
      finishInbound();
    } else if (
      isBackForwardNavigation() ||
      document.documentElement.classList.contains('ll-pt-active')
    ) {
      playHistoryTransition();
    } else {
      requestAnimationFrame(scrollToTop);
      later(scrollToTop, 50);
      later(scrollToTop, 300);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
